/**
 * Where the Help pages live in each route tree (docs/help/HELP_SECTION_PLAN.md §4).
 *
 * The office app mounts Help at /help; the field app at /field/help, because
 * field-only users and installed-PWA launches never reach the office tree.
 * The same components render both; `mount` decides the links and which guides
 * are listed.
 */

import type { HelpGuideMeta } from './useHelpIndex.js';

export type HelpMount = 'office' | 'field';

export function helpBase(mount: HelpMount): string {
  return mount === 'field' ? '/field/help' : '/help';
}

/** Field Help lists only guides marked `app: field` or `app: both` (HELP-D3). */
export function isListedOn(guide: Pick<HelpGuideMeta, 'app'>, mount: HelpMount): boolean {
  return mount === 'office' || guide.app !== 'office';
}

/**
 * Guides link to each other as `/help/<slug>`. On the field mount, point those
 * links at `/field/help/<slug>` so a reader stays in the field app.
 */
export function rebaseHelpLink(href: string, mount: HelpMount): string {
  if (mount !== 'field') {return href;}
  return /^\/help(?=$|[/?#])/.test(href) ? `/field${href}` : href;
}

/** The mount a route belongs to: anything under /field is the field app. */
export function mountForPath(pathname: string): HelpMount {
  return /^\/field(?=$|\/)/.test(pathname) ? 'field' : 'office';
}

/** A "?" hint's Learn more link: `/help/<guide>#<anchor>`, or `/field/help/…` in the field tree. */
export function hintHref(hint: { guide: string; anchor: string }, pathname: string): string {
  return `${helpBase(mountForPath(pathname))}/${hint.guide}#${hint.anchor}`;
}
